import { useState, useEffect, useCallback } from 'react';
import { api } from '../services/api';
import { useAccount } from './useAccount';
import { useWebSocket } from '../contexts/WebSocketContext';

/**
 * usePendingCount Hook
 *
 * Fetches and tracks the count of pending approval items
 * Auto-updates via WebSocket events and polling fallback
 */
export const usePendingCount = () => {
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const { selectedAccountId } = useAccount();
  const { subscribe, isConnected } = useWebSocket();

  const fetchCount = useCallback(async () => {
    // Skip if no account selected (backend requires instagram_account_id)
    if (!selectedAccountId) {
      setCount(0);
      setLoading(false);
      return;
    }

    try {
      // Use getApprovals to count both images AND videos
      const result = await api.getApprovals({
        instagram_account_id: selectedAccountId,
        type: 'all',
        limit: 100
      });
      setCount(result?.items?.length || 0);
    } catch (error) {
      console.error('Failed to fetch pending count:', error);
      setCount(0);
    } finally {
      setLoading(false);
    }
  }, [selectedAccountId]);

  // Initial fetch
  useEffect(() => {
    fetchCount();
  }, [fetchCount]);

  // WebSocket event listeners for live updates
  useEffect(() => {
    if (!isConnected) return;

    const handleUpdate = () => {
      // Refetch count when caption events occur
      fetchCount();
    };

    const unsubscribers = [
      subscribe?.('CaptionGenerated', handleUpdate),
      subscribe?.('CaptionRegenerated', handleUpdate),
      subscribe?.('CaptionApproved', handleUpdate),
    ].filter(Boolean);

    return () => {
      unsubscribers.forEach((unsubscribe) => unsubscribe && unsubscribe());
    };
  }, [isConnected, subscribe, fetchCount]);

  // Polling fallback (every 30 seconds when not connected to WebSocket)
  useEffect(() => {
    if (isConnected) return;

    const interval = setInterval(() => {
      fetchCount();
    }, 30000); // 30 seconds

    return () => clearInterval(interval);
  }, [isConnected, fetchCount]);

  return { count, loading, refresh: fetchCount };
};

export default usePendingCount;
